import { useMutation, useQuery } from '@tanstack/react-query';
import { JsonApiObject } from 'json-api-normalizer';
import toast from 'react-hot-toast';

import { queryClient } from '../../../shared/providers/react-query-provider';
import { getEnvRequired } from '../../../shared/utilities/env';
import { ApiClient } from '../../shared/api/client';

export type TagAttributes = { name: string; color: string };
export interface IncludedTag extends JsonApiObject {
    type: 'tag';
    id: string;
    attributes: TagAttributes;
}

export type AddTagResponse = {
    jsonapi: { version: '1.0' };
    data: IncludedTag;
};

export type GetAllTagResponse = {
    jsonapi: { version: '1.0' };
    data: IncludedTag[];
};

const apiClient = new ApiClient(true);

const REQUEST_MANAGER_API_URL = getEnvRequired('REQUEST_MANAGER_API_URL');

export const getTagsQueryKey = () => ['tags'];

export const useGetAllTags = () =>
    useQuery<GetAllTagResponse>({
        queryKey: getTagsQueryKey(),
        queryFn: () => {
            const url = `${REQUEST_MANAGER_API_URL}/tag`;
            return apiClient.get(url);
        },
    });

export const useCreateTag = () => {
    return useMutation<AddTagResponse, null, TagAttributes & { dossierId: number }>({
        mutationFn: (tagAttributes) => {
            const url = `${REQUEST_MANAGER_API_URL}/tag`;
            return apiClient.post(url, JSON.stringify(tagAttributes));
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({
                queryKey: getTagsQueryKey(),
            });
        },
        onError: () => {
            toast.error('dossier.notifications.error');
        },
    });
};

export const useDeleteTag = () =>
    useMutation<IncludedTag, null, string>({
        mutationFn: (tagId) => {
            const url = `${REQUEST_MANAGER_API_URL}/tag/${tagId}`;
            return apiClient.delete(url);
        },
        onMutate: async (tagId) => {
            const previousTags = queryClient.getQueryData<GetAllTagResponse>(getTagsQueryKey());
            if (previousTags) {
                queryClient.setQueryData<GetAllTagResponse>(getTagsQueryKey(), {
                    ...previousTags,
                    data: previousTags.data.filter(({ id }) => id !== tagId),
                });
            }
            return { previousTags };
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({
                queryKey: getTagsQueryKey(),
            });
        },
        onError: () => {
            toast.error('dossier.notifications.error');
        },
    });

export const useAssignTagToDossier = () =>
    useMutation<null, null, { tagId: string; dossierId: number }>({
        mutationFn: ({ tagId, dossierId }) => {
            const url = `${REQUEST_MANAGER_API_URL}/tag/${tagId}/dossiers`;
            return apiClient.post(url, JSON.stringify({ dossierId }));
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({
                queryKey: getTagsQueryKey(),
            });
        },
        onError: () => {
            toast.error('dossier.notifications.error');
        },
    });

export const useUnassignTagToDossier = () =>
    useMutation<null, null, { tagId: string; dossierId: number }>({
        mutationFn: ({ tagId, dossierId }) => {
            const url = `${REQUEST_MANAGER_API_URL}/tag/${tagId}/dossiers/${dossierId}`;
            return apiClient.delete(url);
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({
                queryKey: getTagsQueryKey(),
            });
        },
        onError: () => {
            toast.error('dossier.notifications.error');
        },
    });
